import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const COACHING_ROLES = ['head_coach', 'assistant_coach', 'special_position_coach', 'rider', 'talent_rider'];
const GOF_EDIT_ROLES = ['head_coach', 'assistant_coach'];

const CoachingRoleContext = createContext(null);

export const useCoachingRole = () => {
  const context = useContext(CoachingRoleContext);
  if (!context) {
    throw new Error('useCoachingRole must be used within a CoachingRoleProvider');
  }
  return context;
};

export const CoachingRoleProvider = ({ children }) => {
  const { user, isPlatformAdmin } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAssignments = useCallback(async () => {
    if (!user) {
      setAssignments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.getMyCoachingAssignments();
      setAssignments(response.assignments || []);
    } catch (err) {
      console.error('Failed to fetch coaching assignments:', err);
      setError(err.message);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  // Derive all unique coaching roles across all assignments
  const coachingRoles = [...new Set(assignments.flatMap(a => a.roles))];
  const isCoach = assignments.length > 0;

  // Check if user can edit GOF for a specific entity
  const canEditGOF = useCallback((entityId) => {
    if (isPlatformAdmin) return true;
    const assignment = assignments.find(a => a.entityId === entityId);
    if (!assignment) return false;
    return assignment.roles.some(r => GOF_EDIT_ROLES.includes(r));
  }, [assignments, isPlatformAdmin]);

  const value = {
    isCoach,
    assignments,
    coachingRoles,
    loading,
    error,
    refresh: fetchAssignments,
    canEditGOF,
  };

  return (
    <CoachingRoleContext.Provider value={value}>
      {children}
    </CoachingRoleContext.Provider>
  );
};

export { COACHING_ROLES, GOF_EDIT_ROLES };
export default CoachingRoleContext;
